import { type CSSProperties, type MouseEvent, type ReactNode, useRef, useState } from 'react'
import { api } from '../api/client'
import type { Agent, Channel, Message, Organisation, UnreadPlace } from '../api/types'
import { Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { asMessage } from '../direct/DirectView'
import { useSeen } from '../read/useSeen'
import { displayName, roomName } from '../room/format'
import { Stream, streamPadding } from '../room/Stream'
import { keyOf, type OpenRoom, useUnreads } from './useUnreads'

const BUTTON: CSSProperties = {
  flex: 'none', padding: '5px 12px', borderRadius: radius.action, cursor: 'pointer',
  border: '1px solid var(--border-strong)', font: '500 12.5px var(--font-interface)', color: 'var(--text-soft)',
}

const NEW: CSSProperties = { flex: 'none', fontSize: 12.5, color: 'var(--text-meta)' }

const ONE_LINE: CSSProperties = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

/** Above a thread reply's name: the thread it is in, by its first message. */
function ThreadLabel({ headline }: { headline: string }) {
  return (
    <div style={{ ...ONE_LINE, font: '400 11.5px var(--font-interface)', color: 'var(--text-meta)' }}>
      Replied in a thread · <span style={{ fontWeight: 600, color: 'var(--text-dim)' }}>{headline}</span>
    </div>
  )
}

/**
 * Everything unread in the organisation, a group per place: on a desktop in
 * the room's place, on a phone a screen of its own. Each group draws its
 * messages with the room's own row, and seeing one here reads it.
 */
export function UnreadsView({
  organisation, counts, channels, roster, myName, compact, openRoom, back, onOpenPlace, onOpenMessage,
}: {
  organisation: Organisation
  /** The organisation stream's total, whose rise fetches the list again. */
  counts: number
  /** Where a room's name comes from. */
  channels: Channel[]
  roster: Agent[]
  myName: string
  compact: boolean
  openRoom?: OpenRoom
  /** A phone's way back. */
  back: ReactNode
  /** A group's Open and what it leaves out: the room at its New line, or the direct message. */
  onOpenPlace: (place: UnreadPlace) => void
  /** Where a message was said: a room's at it, a reply in its thread, a private message in the direct message. */
  onOpenMessage: (place: UnreadPlace, message: Message) => void
}) {
  const { places, failed, unread, newIn, markSeen, markPlaceRead, markAllRead } = useUnreads(organisation.slug, counts, openRoom)
  const scroller = useRef<HTMLDivElement>(null)
  useSeen(scroller, unread, markSeen)
  // The answers given here, which the list holds no reply to show for.
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const total = places?.reduce((sum, place) => sum + newIn(place), 0) ?? 0

  const messagesOf = (place: UnreadPlace): Message[] =>
    (place.kind === 'room' ? place.messages : place.direct_messages.map((message) => asMessage(message, place.nickname)))
      .map((message) => (answers[message.id] ? { ...message, answered_with: answers[message.id] } : message))

  async function answer(place: UnreadPlace, message: Message, choice: string) {
    if (place.kind === 'room') await api.post(place.channel_id, { body: `@${message.sender} ${choice}`, reply_to: message.id })
    else await api.postDirect(organisation.slug, { agent: place.agent, body: choice, sender_type: 'human', also_post: false, reply_to: message.id })
    setAnswers((previous) => ({ ...previous, [message.id]: choice }))
  }

  function open(place: UnreadPlace, messages: Message[], event: MouseEvent<HTMLDivElement>) {
    // A row's own controls, an answer or Show more, do what they say instead.
    if (!(event.target instanceof Element) || event.target.closest('button, a')) return
    const id = event.target.closest('[data-message-id]')?.getAttribute('data-message-id')
    const message = messages.find((candidate) => candidate.id === id)
    if (message) onOpenMessage(place, message)
  }

  const group = (place: UnreadPlace) => {
    const fresh = newIn(place)
    const messages = messagesOf(place)
    const threads = new Map(place.messages.map((message) => [message.id, message.thread]))
    const channel = channels.find((candidate) => candidate.id === place.channel_id)
    const name = place.kind === 'room'
      ? roomName(channel ?? { repository_key: place.repository_key, github_full_name: null })
      : displayName(place.agent, place.nickname)
    return (
      <section key={keyOf(place)} aria-label={name} style={{ padding: '14px 0 10px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {place.kind === 'room' ? (
            <span aria-hidden="true" style={{ flex: 'none', font: '600 15px var(--font-machine)', color: 'var(--text-faint)' }}># </span>
          ) : (
            <span aria-hidden="true" style={{ display: 'flex', flex: 'none' }}>
              <Face name={place.agent} size={20} avatarKey={place.avatar_key} />
            </span>
          )}
          <span
            title={place.repository_key ?? place.agent}
            style={{ ...ONE_LINE, font: `600 15px var(${place.nickname ? '--font-interface' : '--font-machine'})`, letterSpacing: '-.02em' }}
          >
            {name}
          </span>
          {fresh > 0 && <span style={NEW}>{fresh} new</span>}
          <span style={{ flex: 1 }} />
          {!compact && <button type="button" onClick={() => onOpenPlace(place)} className="sp-tap" style={BUTTON}>Open</button>}
          {fresh > 0 && (
            <button type="button" onClick={() => markPlaceRead(place)} className="sp-tap" style={BUTTON}>Mark as read</button>
          )}
        </div>
        {place.earlier > 0 && (
          <button
            type="button"
            onClick={() => onOpenPlace(place)}
            style={{ display: 'block', padding: '10px 0 0 40px', cursor: 'pointer', fontSize: 12, color: 'var(--text-meta)' }}
          >
            {place.earlier} earlier · <span style={{ fontWeight: 600, color: 'var(--accent)' }}>Show</span>
          </button>
        )}
        {/* A row holds buttons of its own, so it cannot be one: a tap anywhere on a message opens it, and Open is the keyboard's way. */}
        <div onClick={(event) => open(place, messages, event)} style={{ cursor: 'pointer' }}>
          <Stream
            dayInTime
            messages={messages}
            unread={unread}
            roster={roster}
            channelId={place.channel_id ?? ''}
            selectedId={null}
            myName={myName}
            onAnswer={(message, choice) => answer(place, message, choice)}
            conversation={{
              placeOf: () => null,
              actionsOf: () => null,
              labelOf: (message) => {
                const thread = threads.get(message.id)
                return thread ? <ThreadLabel headline={thread.headline || thread.sender} /> : null
              },
            }}
          />
        </div>
      </section>
    )
  }

  return (
    <section
      aria-label="Unreads"
      style={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        ...(compact
          ? { paddingTop: 'var(--safe-top)', paddingBottom: 'var(--compact-nav-space)' }
          : { background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: radius.lg }),
      }}
    >
      <header style={{
        display: 'flex', alignItems: 'center', gap: 10, flex: 'none', minWidth: 0,
        padding: compact ? '10px 16px 11px' : '14px 20px', borderBottom: '1px solid var(--border)',
      }}>
        {back}
        <h2 style={{ margin: 0, font: `700 ${compact ? 20 : 16}px var(--font-interface)` }}>Unreads</h2>
        {total > 0 && <span style={NEW}>{total} new</span>}
        <span style={{ flex: 1 }} />
        {total > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="sp-tap"
            style={compact ? { padding: 0, cursor: 'pointer', font: '600 13px var(--font-interface)', color: 'var(--accent)' } : BUTTON}
          >
            Mark all read
          </button>
        )}
      </header>
      <div
        ref={scroller}
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: streamPadding(compact),
          overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch',
        }}
      >
        {places?.map(group)}
        {places?.length === 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '40px 20px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontFamily: 'var(--font-human)', fontSize: 17, color: 'var(--text-dim)' }}>You're all caught up</p>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-faint)' }}>Nothing new in {organisation.name}</p>
          </div>
        )}
        {failed && !places && (
          <p role="alert" style={{ margin: 0, padding: '40px 20px', textAlign: 'center', fontSize: 12.5, color: 'var(--text-faint)' }}>
            Could not load what is unread right now. Open the list again to try once more.
          </p>
        )}
      </div>
    </section>
  )
}
